import React, { useEffect, useRef, useState } from "react";
import "../styles/booking.css";
import { db } from "../firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import SmartDateTimePicker from "../components/SmartDateTimePicker";
import { useLocation } from "react-router-dom";
import jsPDF from "jspdf";
import "jspdf-autotable";

// ✅ Vehicle Data (match with Fleet)
const VEHICLES = [
  { id: "wagonr", name: "Wagon R (Hatchback)", rate: 140, img: "/assets/wagonr.webp" },
  { id: "prius", name: "Toyota Prius (Sedan)", rate: 160, img: "/assets/prius.png" },
  { id: "vezel", name: "Honda Vezel (SUV)", rate: 160, img: "/assets/vezel.png" },
  { id: "hiace", name: "Passenger Van (Hiace)", rate: 260, img: "/assets/hiace.png" },
];

export default function BookingForm() {
  const location = useLocation();

  // 🔹 State
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [pickup, setPickup] = useState(null);
  const [drop, setDrop] = useState(null);
  const [distanceKm, setDistanceKm] = useState(0);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [isGoogleReady, setIsGoogleReady] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // 🔹 Refs
  const pickupInputRef = useRef(null);
  const dropInputRef = useRef(null);
  const mapEl = useRef(null);
  const [map, setMap] = useState(null);
  const [pickupMarker, setPickupMarker] = useState(null);
  const [dropMarker, setDropMarker] = useState(null);
  const [routeLine, setRouteLine] = useState(null);

  // ✅ Load Google Maps Places API
  useEffect(() => {
    const timer = setInterval(() => {
      if (window.google?.maps?.places) {
        setIsGoogleReady(true);
        clearInterval(timer);
      }
    }, 200);
    return () => clearInterval(timer);
  }, []);

  // ✅ Initialize Map — restricted to Sri Lanka 🇱🇰
  useEffect(() => {
    if (!isGoogleReady || !mapEl.current) return;
    const g = window.google;
    const sriLankaBounds = { north: 10.2, south: 5.8, west: 79.6, east: 82.0 };

    const m = new g.maps.Map(mapEl.current, {
      center: { lat: 7.8731, lng: 80.7718 },
      zoom: 8,
      restriction: { latLngBounds: sriLankaBounds, strictBounds: true },
      streetViewControl: false,
      fullscreenControl: false,
      mapTypeControl: false,
    });

    setMap(m);
  }, [isGoogleReady]);

  // ✅ Autocomplete — restricted to Sri Lanka 🇱🇰 only
  useEffect(() => {
    if (!isGoogleReady) return;
    const g = window.google;

    const pickupAC = new g.maps.places.Autocomplete(pickupInputRef.current, {
      fields: ["geometry", "formatted_address", "name"],
      componentRestrictions: { country: "LK" },
    });

    pickupAC.addListener("place_changed", () => {
      const place = pickupAC.getPlace();
      if (place.geometry?.location) {
        setPickup({
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
          label: place.formatted_address || place.name,
        });
      } else alert("Please select a valid Sri Lankan pickup location.");
    });

    const dropAC = new g.maps.places.Autocomplete(dropInputRef.current, {
      fields: ["geometry", "formatted_address", "name"],
      componentRestrictions: { country: "LK" },
    });

    dropAC.addListener("place_changed", () => {
      const place = dropAC.getPlace();
      if (place.geometry?.location) {
        setDrop({
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
          label: place.formatted_address || place.name,
        });
      } else alert("Please select a valid Sri Lankan drop location.");
    });
  }, [isGoogleReady]);

  // ✅ Distance Calculation
  useEffect(() => {
    if (!map || !pickup || !drop) return;
    const g = window.google;

    if (!pickupMarker)
      setPickupMarker(new g.maps.Marker({ map, position: pickup, label: "A" }));
    else pickupMarker.setPosition(pickup);

    if (!dropMarker)
      setDropMarker(new g.maps.Marker({ map, position: drop, label: "B" }));
    else dropMarker.setPosition(drop);

    const bounds = new g.maps.LatLngBounds();
    bounds.extend(pickup);
    bounds.extend(drop);
    map.fitBounds(bounds);

    if (routeLine) routeLine.setMap(null);

    const directionsService = new g.maps.DirectionsService();
    const directionsRenderer = new g.maps.DirectionsRenderer({
      map,
      suppressMarkers: false,
      polylineOptions: { strokeColor: "#ff6600", strokeOpacity: 0.9, strokeWeight: 5 },
    });

    directionsService.route(
      { origin: pickup, destination: drop, travelMode: g.maps.TravelMode.DRIVING },
      (result, status) => {
        if (status === "OK" && result.routes.length > 0) {
          directionsRenderer.setDirections(result);
          setRouteLine(directionsRenderer);

          const totalMeters = result.routes[0].legs.reduce(
            (sum, leg) => sum + leg.distance.value,
            0
          );
          setDistanceKm(totalMeters / 1000);
        } else console.error("Directions request failed:", status);
      }
    );
  }, [pickup, drop, map]); // include map so it recalculates properly when map is set

  // ✅ Fare Calculation
  const priceFor = (v) =>
    !distanceKm || distanceKm <= 0 ? 0 : Math.round(distanceKm * v.rate);

  // ✅ Show vehicles when user came from Fleet OR after distance ready
  const canShowVehicles =
    (pickup && drop && distanceKm > 0) || location.state?.vehicleId;

  // ✅ Normalize phone for LK
  const normalizeLKPhone = (raw) => {
    const digits = (raw || "").replace(/\D/g, "");
    if (digits.startsWith("94")) return `+${digits}`;
    if (digits.startsWith("0")) return `+94${digits.slice(1)}`;
    if (digits.startsWith("+"))
      return digits.startsWith("+94") ? digits : `+${digits.replace(/^(\+)?/, "")}`;
    return `+94${digits}`;
  };

  // ✅ Auto-select Vehicle if Coming from Fleet Page
  useEffect(() => {
    if (location.state?.vehicleId && location.key !== "default") {
      setSelectedVehicle(location.state.vehicleId);
      setTimeout(() => {
        const el = document.querySelector(".vehicle-grid");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 600);
    }
  }, [location.state, location.key]);

  // ✅ Submit Booking
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!pickup || !drop) return alert("Please choose pickup and drop locations.");
    if (!selectedVehicle) return alert("Please select a vehicle.");
    if (!phone || !address || !date || !time)
      return alert("Please fill all fields.");

    const chosen = VEHICLES.find((v) => v.id === selectedVehicle);
    const price = priceFor(chosen);
    const refNum = `LDC-${Math.random()
      .toString(36)
      .substring(2, 8)
      .toUpperCase()}`;

    const formattedDate = date ? date.toLocaleDateString() : "";
    const formattedTime = time
      ? time.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
      : "";

    const booking = {
      ref: refNum,
      pickup: pickup.label,
      drop: drop.label,
      date: formattedDate,
      time: formattedTime,
      phone,
      address,
      vehicleType: chosen.name,
      price,
      distanceKm,
      createdAt: serverTimestamp(),
    };

    try {
      setIsSubmitting(true);
      await addDoc(collection(db, "bookings"), booking);

      const smsUrl =
        "https://asia-south1-lakderana-cabs.cloudfunctions.net/sendBookingSMS";

      // 1️⃣ CUSTOMER SMS (no address → treated as customer)
      const smsResponse = await fetch(smsUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          phone: normalizeLKPhone(phone),
          refNumber: refNum,
          pickup: pickup.label,
          drop: drop.label,
          date: formattedDate,
          time: formattedTime,
           customerPhone: normalizeLKPhone(phone),   // ⭐ ADD THIS
          // NO address field here → customer message template
        }),
      });

      if (!smsResponse.ok)
        throw new Error(`Customer SMS API failed: ${smsResponse.status}`);

      const smsResult = await smsResponse.json();
      if (!smsResult.success) {
        alert("Booking saved, but customer SMS sending failed.");
      }

      // 2️⃣ ADMIN SMS (has address → treated as admin)
      await fetch(smsUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          phone: normalizeLKPhone("0773662322"), // admin number
          refNumber: refNum,
          pickup: pickup.label,
          drop: drop.label,
          date: formattedDate,
          time: formattedTime,
          address, // presence of address → ADMIN template on backend
          customerPhone: normalizeLKPhone(phone), // ✔️ HERE
        }),
      });

      setIsSuccess(true);
    } catch (err) {
      console.error("Booking Error:", err);
      alert(`❌ Booking failed: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ✅ Success Screen
  if (isSuccess) {
    return (
      <div className="booking-success">
        <div className="success-icon">✅</div>
        <h2>🎉 Thank you for booking with Lak Derana Cabs!</h2>
        <p>Your booking has been successfully confirmed.</p>
        <p>We’ll contact you shortly to confirm your ride details.</p>
      </div>
    );
  }

  // ✅ Main UI
  return (
    <div className="booking-page">
      <div className="booking-left">
        <h2>Book Your Ride</h2>

        <input type="text" placeholder="Pickup Location" ref={pickupInputRef} />
        <input type="text" placeholder="Drop Location" ref={dropInputRef} />

        {distanceKm > 0 && (
          <div className="distance-text">
            <p>
              Estimated distance: <strong>{distanceKm.toFixed(1)} km</strong>
            </p>
          </div>
        )}

        {selectedVehicle && (
          <div className="selected-banner">
            Selected: {VEHICLES.find((v) => v.id === selectedVehicle)?.name}
          </div>
        )}

        {canShowVehicles && (
          <div className="vehicle-grid">
            {VEHICLES.map((v) => {
              const price = priceFor(v);
              const active = selectedVehicle === v.id;
              return (
                <button
                  key={v.id}
                  type="button"
                  className={`vehicle-card ${active ? "active" : ""}`}
                  onClick={() => setSelectedVehicle(v.id)}
                >
                  <img src={v.img} alt={v.name} loading="lazy" />
                  <div className="vehicle-name">{v.name}</div>
                  {distanceKm > 0 && (
                    <div className="vehicle-price">
                      Rs. {price.toLocaleString()}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}

        <SmartDateTimePicker
          date={date}
          time={time}
          onDate={setDate}
          onTime={setTime}
        />

        <input
          type="tel"
          placeholder="Mobile Number"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
        />
        <textarea
          placeholder="Home Address"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
        ></textarea>

        <div className="form-buttons">
          <button type="button" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? "Booking..." : "Confirm Booking"}
          </button>
        </div>
      </div>

      <div className="booking-right">
        <div id="map" ref={mapEl}></div>
      </div>
    </div>
  );
}
